import React, { useRef, useState, useEffect } from 'react';
import { getClientMetadata } from '../../utils/signatureMetadata';
import './SignatureField.css';

const SignatureField = ({ value, onChange, required, label, name }) => {
  const canvasRef = useRef(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [hasSignature, setHasSignature] = useState(false);

  useEffect(() => {
    // If there's a saved signature, draw it on the canvas
    if (value) {
      let imageData = null;
      
      // Handle both old format (just image data URL string) and new format (object with metadata)
      if (typeof value === 'string') {
        if (value.startsWith('{')) {
          try {
            const signatureData = JSON.parse(value);
            imageData = signatureData.image;
          } catch (e) {
            // If parsing fails, assume it's just the image
            imageData = value;
          }
        } else if (value.startsWith('data:image')) {
          imageData = value;
        }
      } else if (typeof value === 'object' && value.image) {
        // New format - object with image and metadata
        imageData = value.image;
      }
      
      if (imageData && imageData.startsWith('data:image')) {
        const canvas = canvasRef.current;
        if (canvas) {
          const ctx = canvas.getContext('2d');
          const img = new Image();
          img.onload = () => {
            ctx.clearRect(0, 0, canvas.width, canvas.height);
            ctx.drawImage(img, 0, 0);
            setHasSignature(true);
          };
          img.src = imageData;
        }
      } else {
        setHasSignature(false);
      }
    } else {
      setHasSignature(false);
    }
  }, [value]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');

    // Set high quality rendering for crisp lines
    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = 'high';

    ctx.strokeStyle = '#000000';
    ctx.lineWidth = 1;  // Reduced from 2 for smoother lines on smaller canvas
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';

    const getCoordinates = (e) => {
      const rect = canvas.getBoundingClientRect();
      const scaleX = canvas.width / rect.width;
      const scaleY = canvas.height / rect.height;
      
      if (e.touches && e.touches.length > 0) {
        // Touch event
        return {
          x: (e.touches[0].clientX - rect.left) * scaleX,
          y: (e.touches[0].clientY - rect.top) * scaleY
        };
      } else {
        // Mouse event
        return {
          x: (e.clientX - rect.left) * scaleX,
          y: (e.clientY - rect.top) * scaleY
        };
      }
    };

    const startDrawing = (e) => {
      e.preventDefault();
      setIsDrawing(true);
      const coords = getCoordinates(e);
      ctx.beginPath();
      ctx.moveTo(coords.x, coords.y);
    };

    const draw = (e) => {
      e.preventDefault();
      if (!isDrawing) return;

      const coords = getCoordinates(e);

      // Ensure coordinates are within canvas bounds
      if (coords.x >= 0 && coords.x <= canvas.width &&
          coords.y >= 0 && coords.y <= canvas.height) {
        ctx.lineTo(coords.x, coords.y);
        ctx.stroke();
        if (!hasSignature) {
          setHasSignature(true);
        }
      }
    };

    const stopDrawing = (e) => {
      e.preventDefault();
      if (!isDrawing) return;
      setIsDrawing(false);

      // Save the signature after a short delay so the canvas is fully updated.
      // Nothing in here may be allowed to block the save: previously this awaited
      // geolocation-backed metadata before calling onChange, so an unanswered
      // browser permission prompt meant the signature was silently never stored
      // and the parent was told "Please sign the application before submitting."
      // Metadata is best-effort; the signature itself is not.
      setTimeout(() => {
        const canvas = canvasRef.current;
        if (!canvas || !onChange) return;

        let metadata = {};
        try {
          metadata = getClientMetadata();
        } catch (err) {
          // Metadata is a nice-to-have; never lose a signature over it.
          console.warn('Signature metadata unavailable:', err);
        }

        // Stored as an object - JSONB handles it. The authoritative IP address,
        // signing user, and server timestamp are stamped server-side at submit.
        onChange({
          image: canvas.toDataURL('image/png'),
          metadata: {
            ...metadata,
            capturedAt: new Date().toISOString()
          }
        });
      }, 100);
    };

    // Mouse events
    canvas.addEventListener('mousedown', startDrawing);
    canvas.addEventListener('mousemove', draw);
    canvas.addEventListener('mouseup', stopDrawing);
    canvas.addEventListener('mouseout', stopDrawing);

    // Touch events
    canvas.addEventListener('touchstart', startDrawing, { passive: false });
    canvas.addEventListener('touchmove', draw, { passive: false });
    canvas.addEventListener('touchend', stopDrawing, { passive: false });
    canvas.addEventListener('touchcancel', stopDrawing, { passive: false });

    return () => {
      canvas.removeEventListener('mousedown', startDrawing);
      canvas.removeEventListener('mousemove', draw);
      canvas.removeEventListener('mouseup', stopDrawing);
      canvas.removeEventListener('mouseout', stopDrawing);
      canvas.removeEventListener('touchstart', startDrawing);
      canvas.removeEventListener('touchmove', draw);
      canvas.removeEventListener('touchend', stopDrawing);
      canvas.removeEventListener('touchcancel', stopDrawing);
    };
  }, [isDrawing]);

  const clearSignature = () => {
    const canvas = canvasRef.current;
    if (canvas) {
      const ctx = canvas.getContext('2d');
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      setHasSignature(false);
      if (onChange) {
        onChange('');
      }
    }
  };

  return (
    <div className="signature-field">
      <div className="signature-canvas-container">
        <canvas
          ref={canvasRef}
          width={500}
          height={80}
          className="signature-canvas"
        />
        {hasSignature && (
          <button
            type="button"
            onClick={clearSignature}
            className="signature-clear-btn"
          >
            Clear
          </button>
        )}
      </div>
      <p className="signature-hint">
        Sign above using your mouse, trackpad, or finger (on touchscreen)
      </p>
    </div>
  );
};

export default SignatureField;

